import { MeasurementGroupColumns, MgLine, MgLoad, MgPlant, QueryMeasurementGroupsArgs } from './types';
import { QueryClient } from '@tanstack/react-query';
import { Params } from '@/utils/types';
import { buildQueryParams } from '@/utils/build-query-params';
import { MEASUREMENT_GROUPS_BASE_API_URL } from '../constants';
import apiClient from '@/api';
import { getMinifiedProcessObjects } from '@/routes/process-objects/list/constants';
import { TIME_CONSTANTS_IN_MS } from '@/constants';

export const PAGE_SIZES = ['10', '20', '50', '100'];
export const DEFAULT_PAGE_SIZE = '100';
export const MG_LINES_BASE_API_URL = '/api/v1/mg-lines';
export const MG_LOADS_BASE_API_URL = '/api/v1/mg-loads';
export const MG_PLANTS_BASE_API_URL = '/api/v1/mg-plants';

function queryMeasurementGroups(params: QueryMeasurementGroupsArgs) {
	const { limit, sortDirection, sortBy, page, search } = params;

	const queryParams = buildQueryParams(params, MeasurementGroupColumns);

	return {
		queryKey: ['measurement-groups', limit, sortBy, sortDirection, page, search],
		queryFn: () => apiClient.get(`${MEASUREMENT_GROUPS_BASE_API_URL}?${queryParams}`)
	};
}

function getMgLines() {
	return {
		queryKey: ['mg-lines'],
		queryFn: () => apiClient.get<MgLine[]>(MG_LINES_BASE_API_URL)
	};
}

function getMgLoads() {
	return {
		queryKey: ['mg-loads'],
		queryFn: () => apiClient.get<MgLoad[]>(MG_LOADS_BASE_API_URL)
	};
}

function getMgPlants() {
	return {
		queryKey: ['mg-plants'],
		queryFn: () => apiClient.get<MgPlant[]>(MG_PLANTS_BASE_API_URL)
	};
}

export function measurementGroupListLoader(queryClient: QueryClient) {
	return async ({ request }: { request: Request }) => {
		const url = new URL(request.url);

		const page = url.searchParams.get(Params.page) || '1';
		const limit = url.searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
		const sortBy = url.searchParams.get(Params.sortBy) || '';
		const sortDirection = url.searchParams.get(Params.sortDirection) || '';
		const search = url.searchParams.get(Params.search) || '';

		const queryParams = {
			page,
			limit,
			sortBy,
			sortDirection,
			search
		};

		const measurementGroupList = await queryClient.fetchQuery({
			...queryMeasurementGroups(queryParams),
			staleTime: TIME_CONSTANTS_IN_MS.FIVE_MIN
		});

		const minifiedProcessObjects = queryClient.ensureQueryData(getMinifiedProcessObjects());

		const mgLineList = queryClient.ensureQueryData(getMgLines());
		const mgLoadList = queryClient.ensureQueryData(getMgLoads());
		const mgPlantList = queryClient.ensureQueryData(getMgPlants());

		return {
			measurementGroupList: measurementGroupList.data,
			minifiedProcessObjects,
			mgLineList,
			mgLoadList,
			mgPlantList
		};
	};
}
